/*eslint-disable*/

import React, { useState, useEffect, useRef } from "react";
import {
    TextField,
    IconButton,
    Button,
    Box,
    Typography,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    SelectChangeEvent,
    CircularProgress,
} from "@mui/material";
import { HiPencil, HiOutlineX } from "react-icons/hi";
import LocationSearch from "../../Search/LocationSearch";
import EditableTextField from "../../Input/EditableTextField";
import CustomIconButton from "../../Buttons/CustomIconButton";

import FastAPIClient from "../../../client";
import config from "../../../config";

const client = new FastAPIClient(config);

interface User {
    first_name: string;
    last_name: string;
    complete_address: string;
    address: string;
    city: string;
    zip_code: string;
    start_location: string;
    email: string;
    phone: string;
}

interface PersonalDetailsProps {
    user: User;
    onSave: (updatedUser: User) => void;
}

const PersonalDetails: React.FC<PersonalDetailsProps> = ({ user }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [userData, setUserData] = useState(user);

    // Store the initial user data in a ref to preserve it across re-renders
    const initialUserData = useRef(user);

    const handleClose = () => {
        setIsEditing(false);
        setUserData(initialUserData.current); // Reset to original data when close is clicked
    };

    const [errors, setErrors] = useState({
        address: "",
        city: "",
        zip_code: "",
        first_name: "",
    });

    // handleChange now uses event correctly
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const updatedData = { ...userData, [name]: value };

        // Update complete_address if address-related fields change
        if (name === "address" || name === "city" || name === "zip_code") {
            const { address, city, zip_code } = updatedData;
            if (address && city && zip_code) {
                updatedData.complete_address = `${address}, ${zip_code} ${city}`;
            }
        }

        setUserData(updatedData);
    };

    // handleStartLocationChange is correctly typed to receive SelectChangeEvent
    const handleStartLocationChange = (e: SelectChangeEvent<string>) => {
        setUserData({ ...userData, start_location: e.target.value });
    };

    const [isSaving, setIsSaving] = useState(false); // New state for tracking save process

    // No need for 'updatedUser' since we're directly passing 'userData' to 'onSave'
    const handleSave = async () => {
        let hasError = false;
        const newErrors = {
            address: "",
            city: "",
            zip_code: "",
            first_name: "",
        };

        if (userData.start_location === "HOME") {
            if (!userData.address) {
                newErrors.address = "L'adresse est requise";
                hasError = true;
            }
            if (!userData.city) {
                newErrors.city = "La ville est requise";
                hasError = true;
            }
            if (!userData.zip_code) {
                newErrors.zip_code = "Le code postal est requis";
                hasError = true;
            }
        }

        if (!userData.first_name) {
            newErrors.first_name = "Le prénom est requis";
            hasError = true;
        }

        if (hasError) {
            setErrors(newErrors); // Update error state
            return; // Prevent saving if there are validation errors
        }

        // Set loading state
        setIsSaving(true);

        // Proceed with saving the user data
        try {
            await updateUser(userData);
            setErrors({ address: "", city: "", zip_code: "", first_name: "" }); // Clear errors on successful save
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating user:", error);
        } finally {
            // Reset loading state
            setIsSaving(false);
        }
    };

    const [forceUpdate, setForceUpdate] = useState(0);

    // Function to handle selected address data
    const handleAddressSelect = (
        street: string,
        city: string,
        zipCode: string
    ) => {
        const complete_address = `${street}, ${zipCode} ${city}`;
        setUserData({
            ...userData,
            complete_address,
            address: street,
            city,
            zip_code: zipCode,
        });
        setForceUpdate((prev) => prev + 1); // Increment key to force re-render
    };

    const updateUser = async (userUpdate) => {
        try {
            const response = await client.updateUser(
                userUpdate.user_id,
                userUpdate
            );
            // Handle successful response, like updating state or UI
        } catch (error) {
            console.error("Error updating user:", error);
            // Handle error, show notification, etc.
        }
    };

    const startLocationOptions = [
        { value: "DEPOT", label: "Dépôt" },
        { value: "HOME", label: "Personnalisé", actualValue: "HOME" },
    ];

    return (
        <div className="w-full md:w-1/2">
            {/* Header with Edit Icon */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
            >
                <Typography variant="h6">Informations personnelles</Typography>
                {!isEditing && (
                    <CustomIconButton
                        onClick={() => setIsEditing(true)}
                        icon={<HiPencil />}
                    />
                )}
                {isEditing && (
                    <CustomIconButton
                        onClick={handleClose}
                        icon={<HiOutlineX />}
                    />
                )}
            </Box>

            {/* Personal Details Form */}
            <Box display="flex" flexDirection="column" gap={2}>
                <EditableTextField
                    label="Prénom"
                    name="first_name"
                    value={userData.first_name}
                    isEditing={isEditing}
                    error={!!errors.first_name}
                    helperText={errors.first_name}
                    onChange={handleChange}
                />

                <EditableTextField
                    label="Nom"
                    name="last_name"
                    value={userData.last_name}
                    isEditing={isEditing}
                    error={false}
                    helperText=""
                    onChange={handleChange}
                />

                <EditableTextField
                    label="Téléphone"
                    name="phone"
                    value={userData.phone}
                    isEditing={isEditing}
                    error={false}
                    helperText=""
                    onChange={handleChange}
                />

                <EditableTextField
                    label="Email"
                    name="email"
                    value={userData.email}
                    isEditing={false}
                    isEditable={false}
                    error={false} // No error for last name
                    helperText=""
                    onChange={handleChange}
                />

                {/* Start Location Dropdown */}

                <FormControl
                    fullWidth
                    className=""
                    sx={{ position: "relative" }}
                >
                    <InputLabel
                        sx={{
                            fontSize: "1rem", // Set the font size to match TextField
                            position: "absolute", // Position the label absolutely inside the FormControl
                            top: "8px", // Adjust the top position for proper alignment with the input field
                            left: -15, // Ensure it starts at the very left
                            marginLeft: 0, // Remove any left margin (if applied by default)
                            "&.MuiInputLabel-shrink": {
                                top: -6, // Shrink label and move up when the input is focused or has a value
                            },
                        }}
                        className="mt-3 text-gray-100"
                    >
                        Lieu de départ
                    </InputLabel>
                    <Select
                        label="Lieu de départ"
                        name="start_location"
                        value={userData.start_location}
                        onChange={handleStartLocationChange}
                        fullWidth
                        variant="standard"
                        disabled={!isEditing} // Disable editing when isEditing is false
                        disableUnderline={!isEditing} // Remove the underline when disabled
                        sx={{
                            input: {
                                color: "black !important", // Force black text even when disabled
                                textAlign: "left", // Left-align the text inside the Select box
                            },
                            "& .MuiInputBase-input": {
                                textAlign: "left", // Left-align the selected value text
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black", // Ensure black text when disabled
                            },
                            // Remove the bottom border when disabled
                            "& .MuiInput-underline:before": {
                                borderBottom: !isEditing
                                    ? "none !important"
                                    : "1px solid rgba(0, 0, 0, 0.42)", // Remove underline when not editing or disabled
                            },
                            // Remove the bottom border after focus (when disabled)
                            "& .MuiInput-underline:after": {
                                borderBottom: !isEditing
                                    ? "none !important"
                                    : "1px solid rgba(0, 0, 0, 0.42)", // Remove underline after focus when disabled
                            },
                            // Remove the dotted border specifically when the component is disabled
                            "& .MuiInput-underline.Mui-disabled:before": {
                                borderBottom: "none !important", // Completely remove the dotted border when disabled
                            },
                            // Ensure no hover effect when disabled
                            "& .MuiInput-underline:hover:before": {
                                borderBottom: !isEditing
                                    ? "none !important"
                                    : "1px solid rgba(0, 0, 0, 0.42)",
                            },
                            boxShadow: "none",
                            ".MuiOutlinedInput-notchedOutline": { border: 0 },
                        }}
                    >
                        {startLocationOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Conditionally Render Address, City, and Zip Code Fields */}
                {userData.start_location === "HOME" && (
                    <>
                        {isEditing && (
                            <LocationSearch
                                onAddressSelect={handleAddressSelect}
                            />
                        )}

                        <EditableTextField
                            label="Address"
                            name="address"
                            value={userData.address}
                            isEditing={isEditing}
                            error={!!errors.address} // Display error if present
                            helperText={errors.address} // Show the error message
                            forceUpdateKey={forceUpdate} // Pass force update key
                            onChange={handleChange}
                        />

                        <EditableTextField
                            label="Code postal"
                            name="zip_code"
                            value={userData.zip_code}
                            isEditing={isEditing}
                            error={!!errors.zip_code} // Display error if present
                            helperText={errors.zip_code} // Show the error message
                            forceUpdateKey={forceUpdate} // Pass force update key
                            onChange={handleChange}
                        />

                        <EditableTextField
                            label="City"
                            name="city"
                            value={userData.city}
                            isEditing={isEditing}
                            error={!!errors.city} // Display error if present
                            helperText={errors.city} // Show the error message
                            forceUpdateKey={forceUpdate} // Pass force update key
                            onChange={handleChange}
                        />
                    </>
                )}
            </Box>

            {/* Save & Cancel Buttons (Only in Edit Mode) */}
            {isEditing && (
                <Box
                    mt={2}
                    display="flex"
                    justifyContent="flex-end"
                    sx={{ paddingBottom: "2.5rem" }}
                >
                    <Button
                        onClick={handleSave}
                        disabled={!isEditing || isSaving} // Disable button while saving
                        sx={{
                            borderRadius: "999px",
                            backgroundColor: isEditing ? "#C3FF36" : "gray",
                            color: "black",
                            fontWeight: "bold",
                            "&:disabled": {
                                backgroundColor: "lightgray",
                                color: "darkgray",
                            },
                        }}
                    >
                        {isSaving ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: "black" }}
                            />
                        ) : (
                            "Sauvegarder"
                        )}
                    </Button>
                </Box>
            )}
        </div>
    );
};

export default PersonalDetails;
